import React from 'react';
import { Box, Typography, Grid, Paper } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SeagullBlack from '../images/04-black.webp';
import SeagullBlue from '../images/02-blue.webp';
import SeagullWhite from '../images/03-white.webp';
import SeagullPink from '../images/01-pink.webp';

function WarnaSeagull() {
  return (
    <Box
      sx={{ padding: { xs: 2, sm: 4 }, maxWidth: '1200px', margin: '0 auto' }}
    >
      <Helmet>
        <title>
          Pilihan Warna BYD Atto1 2025 - Mana yang Cocok untuk Kamu?
        </title>
        <meta
          name='description'
          content='Lihat semua varian warna BYD Atto1 2025 di BYD BSD Serpong: Black, Blue, Pink, dan White. Pilih warna favoritmu dan temukan kepribadian mobil listrik impianmu!'
        />
        <meta
          name='keywords'
          content='Warna BYD Atto1, Warna Mobil BYD Atto1, Atto1 Pink, Atto1 putih, Atto1 biru, Atto1 hitem, BYD BSD Serpong'
        />
        <link
          rel='canonical'
          href='https://bydbsdserpong.com/blog/pilihan-warna-byd-seagull'
        />
      </Helmet>
      <br />
      <br />
      <br />
      <br />
      <Typography variant='h3' component='h1' fontWeight='bold' gutterBottom>
        Pilihan Warna BYD Atto1 2025: Mana yang Cocok untuk Kamu?
      </Typography>

      <Typography
        variant='body1'
        paragraph
        sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
      >
        BYD Atto1 2025 bukan hanya menawarkan harga terjangkau dan teknologi
        canggih, tetapi juga pilihan warna yang bisa mencerminkan kepribadian
        kamu! ✨ Tersedia dalam empat warna utama—
        <strong>Black, Blue, White, dan Pink</strong>—setiap varian punya kesan
        tersendiri yang bisa bikin kamu jatuh cinta sejak pandangan pertama.
        Artikel ini akan mengupas tuntas makna, kelebihan visual, dan
        rekomendasi berdasarkan gaya hidup untuk tiap warna Atto1.
      </Typography>

      <Grid container spacing={4} sx={{ mt: 4 }}>
        {[
          {
            title: '🖤 BYD Atto1 Black',
            image: SeagullBlack,
            desc: `Warna hitam selalu identik dengan kemewahan, kekuatan, dan kesan profesional. BYD Atto1 dalam warna black cocok buat kamu yang ingin tampil elegan dan misterius di jalanan kota.`,
          },
          {
            title: '💙 BYD Atto1 Blue',
            image: SeagullBlue,
            desc: `Warna biru memberikan kesan segar, modern, dan futuristic. Cocok untuk pengemudi muda yang suka tampil beda dan percaya diri. Ini adalah pilihan warna yang cocok untuk mahasiswa atau pekerja startup.`,
          },
          {
            title: '🤍 BYD Atto1 White',
            image: SeagullWhite,
            desc: `Putih adalah simbol dari kesederhanaan dan kebersihan. Dengan warna ini, BYD Atto1 terlihat seperti mobil masa depan yang sleek dan elegan.`,
          },
          {
            title: '🎀 BYD Atto1 Pink',
            image: SeagullPink,
            desc: `Buat kamu yang berani tampil playful dan penuh semangat, varian pink adalah pilihan terbaik! Warna ini sangat disukai Gen Z dan cocok buat kamu yang ingin tampil standout.`,
          },
        ].map((color, index) => (
          <Grid item xs={12} sm={6} key={index}>
            <Paper
              elevation={3}
              sx={{ padding: 3, textAlign: 'center', borderRadius: 3 }}
            >
              <Box
                component='img'
                src={color.image}
                alt={color.title}
                sx={{
                  width: '100%',
                  maxHeight: 220,
                  objectFit: 'contain',
                  mb: 2,
                }}
              />
              <Typography variant='h5' fontWeight='bold' gutterBottom>
                {color.title}
              </Typography>
              <Typography variant='body1' sx={{ lineHeight: 1.8 }}>
                {color.desc}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Kenapa Warna Penting dalam Memilih Mobil?
        </Typography>
        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          Menurut berbagai studi, warna mobil dapat mencerminkan kepribadian dan
          mempengaruhi mood saat berkendara. Pemilik mobil hitam dianggap lebih
          berkelas, sementara pemilik mobil putih lebih perfeksionis dan
          praktis. Warna pink menunjukkan keunikan dan ekspresi diri, sedangkan
          biru menggambarkan ketenangan dan kecerdasan.
        </Typography>
        <Typography
          variant='body1'
          paragraph
          sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}
        >
          Selain itu, warna juga bisa berpengaruh pada nilai jual kembali dan
          visibilitas di jalan. Misalnya, warna putih lebih mudah terlihat di
          malam hari, sementara warna hitam sering terlihat lebih cepat kotor
          namun tetap jadi favorit karena tampilannya yang maskulin dan
          eksklusif.
        </Typography>
      </Box>

      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          Tips Memilih Warna Atto1 Berdasarkan Kepribadian
        </Typography>
        <ul style={{ fontSize: '1.1rem', lineHeight: 2 }}>
          <li>
            <strong>Ekstrovert & Ceria:</strong> Pilih <strong>Pink</strong>{' '}
            untuk tampil menonjol dan penuh energi
          </li>
          <li>
            <strong>Minimalis & Rapi:</strong> Warna <strong>Putih</strong>{' '}
            cocok banget untuk kamu yang suka hal simpel dan bersih
          </li>
          <li>
            <strong>Stylish & Profesional:</strong> Warna <strong>Hitam</strong>{' '}
            cocok buat kamu yang ingin tampil elegan dan misterius
          </li>
          <li>
            <strong>Smart & Teknologis:</strong> Warna <strong>Biru</strong>{' '}
            ideal untuk kamu yang melek teknologi dan suka hal futuristik
          </li>
        </ul>
      </Box>

      {/* Komentar Pelanggan Fiktif */}
      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          🗣️ Komentar Pelanggan Kami
        </Typography>
        <Grid container spacing={3} mt={2}>
          {[
            '“Warna pink-nya gemes banget! Saya langsung jatuh cinta sejak pertama lihat. Teman-teman saya juga pada iri.” — Rani, 24 tahun, Jakarta',
            '“Saya pilih warna putih karena terlihat futuristik dan bersih banget. Cocok banget buat tampil profesional.” — Andi, 29 tahun, Tangerang',
            '“Biru jadi pilihan saya karena beda dan mewakili jiwa muda saya. Mobilnya kecil, tapi gayanya besar!” — Raka, 21 tahun, Bandung',
            '“Awalnya ragu sama warna hitam, tapi setelah lihat langsung, elegan banget. Cocok buat daily use ke kantor.” — Siska, 33 tahun, Depok',
          ].map((comment, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Paper
                elevation={1}
                sx={{ p: 3, borderLeft: '4px solid #1976d2' }}
              >
                <Typography variant='body1' sx={{ fontStyle: 'italic' }}>
                  {comment}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ mt: 6 }}>
        <Typography variant='h4' fontWeight='bold' gutterBottom>
          ❓ Pertanyaan yang Sering Ditanyakan (FAQ)
        </Typography>
        <Box sx={{ mt: 3 }}>
          {[
            {
              q: 'Apakah warna mempengaruhi harga mobil?',
              a: 'Tidak, seluruh varian warna BYD Atto1 saat ini memiliki harga yang sama. Namun, tergantung permintaan dan tren pasar, warna tertentu bisa jadi lebih langka dan lebih cepat habis.',
            },
            {
              q: 'Apa warna favorit Gen Z untuk mobil listrik?',
              a: 'Menurut survei internal kami, warna Pink dan Biru adalah pilihan terfavorit Gen Z. Keduanya merepresentasikan gaya hidup yang bold, berbeda, dan berani tampil.',
            },
            {
              q: 'Warna apa yang paling mudah dirawat?',
              a: 'Warna putih dan biru cenderung lebih mudah dirawat karena tidak terlalu terlihat saat kotor, berbeda dengan warna hitam yang sering menampilkan goresan kecil dan debu.',
            },
          ].map((item, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography variant='h6' fontWeight='bold'>
                {item.q}
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontSize: '1.05rem', lineHeight: 1.8 }}
              >
                {item.a}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* CTA section */}
      <Paper
        sx={{
          p: 4,
          mt: 6,
          backgroundColor: '#e8f5e9',
          borderLeft: '6px solid #43a047',
          borderRadius: 2,
        }}
      >
        <Typography variant='h5' fontWeight='bold' gutterBottom>
          🎨 Sudah Tentukan Warna Favoritmu?
        </Typography>
        <Typography variant='body1' gutterBottom>
          Hubungi kami sekarang dan pilih warna BYD Atto1 yang paling cocok
          dengan karaktermu!
        </Typography>
        <Typography variant='body2' sx={{ mt: 1 }}>
          📞{' '}
          <a
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20BYD%20Seagull%20warna'
            target='_blank'
            rel='noopener noreferrer'
          >
            Chat WhatsApp
          </a>{' '}
          atau follow kami di{' '}
          <a
            href='https://instagram.com/bydbsdserpong'
            target='_blank'
            rel='noopener noreferrer'
          >
            @bydbsdserpong
          </a>{' '}
          untuk info lebih lanjut dan update terbaru!
        </Typography>
      </Paper>
    </Box>
  );
}

export default WarnaSeagull;
